/**
 * Field Type Registry
 *
 * Central registry of all prm-*-field types available in the Form Builder palette.
 * Maps field types to their metadata, compatible column types, and grouping.
 */

import type {
    PrmFieldType,
    PrmFieldTypeDefinition,
} from "modules/service-portal/form-builder/types/formBuilder.types";

export const FIELD_TYPE_REGISTRY: PrmFieldTypeDefinition[] = [
    // ── Text ──
    {
        type: "text",
        tag: "prm-text-field",
        label: "Text",
        icon: "text_fields",
        group: "text",
        compatibleColumnTypes: ["TEXT"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "textarea",
        tag: "prm-textarea-field",
        label: "Text Area",
        icon: "notes",
        group: "text",
        compatibleColumnTypes: ["TEXT"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "email",
        tag: "prm-email-field",
        label: "Email",
        icon: "email",
        group: "text",
        compatibleColumnTypes: ["TEXT"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "url",
        tag: "prm-url-field",
        label: "URL",
        icon: "link",
        group: "text",
        compatibleColumnTypes: ["TEXT"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "password",
        tag: "prm-password-field",
        label: "Password",
        icon: "lock",
        group: "text",
        compatibleColumnTypes: ["PASSWORD"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "richtext",
        tag: "prm-richtext-field",
        label: "Rich Text",
        icon: "format_align_left",
        group: "text",
        compatibleColumnTypes: ["RICH_HTML", "TEXT"],
        hasOptions: false,
        hasSource: false,
    },

    // ── Numbers ──
    {
        type: "number",
        tag: "prm-number-field",
        label: "Number",
        icon: "tag",
        group: "numbers",
        compatibleColumnTypes: ["INTEGER"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "decimal",
        tag: "prm-decimal-field",
        label: "Decimal",
        icon: "decimal_increase",
        group: "numbers",
        compatibleColumnTypes: ["DECIMAL", "FLOAT"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "currency",
        tag: "prm-currency-field",
        label: "Currency",
        icon: "attach_money",
        group: "numbers",
        compatibleColumnTypes: ["CURRENCY"],
        hasOptions: false,
        hasSource: false,
    },
    {
        type: "boolean",
        tag: "prm-boolean-field",
        label: "Checkbox",
        icon: "check_box",
        group: "numbers",
        compatibleColumnTypes: ["BOOLEAN"],
        hasOptions: false,
        hasSource: false,
        supportsVariant: true,
    },

    // ── Date/Time ──
    {
        type: "date",
        tag: "prm-date-field",
        label: "Date",
        icon: "calendar_today",
        group: "datetime",
        compatibleColumnTypes: ["DATE"],
        hasOptions: false,
        hasSource: false,
        supportsMinMax: true,
    },
    {
        type: "datetime",
        tag: "prm-datetime-field",
        label: "Date & Time",
        icon: "schedule",
        group: "datetime",
        compatibleColumnTypes: ["DATETIME"],
        hasOptions: false,
        hasSource: false,
        supportsMinMax: true,
    },
    {
        type: "time",
        tag: "prm-time-field",
        label: "Time",
        icon: "access_time",
        group: "datetime",
        compatibleColumnTypes: ["TIME"],
        hasOptions: false,
        hasSource: false,
        supportsMinMax: true,
    },

    // ── Selection ──
    {
        type: "choice",
        tag: "prm-choice-field",
        label: "Dropdown",
        icon: "arrow_drop_down_circle",
        group: "selection",
        compatibleColumnTypes: ["CHOICE", "TEXT"],
        hasOptions: true,
        hasSource: true,
    },
    {
        type: "reference",
        tag: "prm-reference-field",
        label: "Reference",
        icon: "search",
        group: "selection",
        compatibleColumnTypes: ["REFERENCE"],
        hasOptions: false,
        hasSource: true,
    },

    // ── Media ──
    {
        type: "attachment",
        tag: "prm-attachment-field",
        label: "File upload",
        icon: "attach_file",
        group: "media",
        compatibleColumnTypes: ["ATTACHMENT"],
        hasOptions: false,
        hasSource: false,
        supportsMultiple: true,
    },
    {
        type: "color",
        tag: "prm-color-field",
        label: "Color Picker",
        icon: "palette",
        group: "media",
        compatibleColumnTypes: ["COLOR_PICKER"],
        hasOptions: false,
        hasSource: false,
    },
];

/** Groups for palette organization */
export const FIELD_TYPE_GROUPS = [
    { key: "text" as const, label: "Text", icon: "text_fields" },
    { key: "numbers" as const, label: "Numbers", icon: "tag" },
    { key: "datetime" as const, label: "Date & Time", icon: "calendar_today" },
    { key: "selection" as const, label: "Selection", icon: "list" },
    { key: "media" as const, label: "Media", icon: "perm_media" },
];

/** Get field type definition by type */
export const getFieldTypeDefinition = (type: PrmFieldType): PrmFieldTypeDefinition | undefined =>
    FIELD_TYPE_REGISTRY.find((def) => def.type === type);

/** Builder-specific keys replace older shared translations with outdated pt-BR wording. */
export const builderFieldTypeLabelKey = (type: PrmFieldType, label: string): string =>
    type === "choice" ? "service_builder.field.dropdown" : type === "boolean" ? "service_builder.field.checkbox" : label;

/** Get all field types grouped */
export const getGroupedFieldTypes = () => {
    const groups = new Map<string, PrmFieldTypeDefinition[]>();
    for (const def of FIELD_TYPE_REGISTRY) {
        const existing = groups.get(def.group) || [];
        existing.push(def);
        groups.set(def.group, existing);
    }
    return groups;
};

/** Get HTML tag name for a field type */
export const getFieldTag = (type: PrmFieldType): string => {
    const def = getFieldTypeDefinition(type);
    return def?.tag || `prm-${type}-field`;
};

/** Resolve field type from HTML tag name */
export const resolveFieldType = (tag: string): PrmFieldType | null => {
    const match = tag.match(/^prm-(.+)-field$/);
    if (!match) return null;
    const type = match[1] as PrmFieldType;
    return getFieldTypeDefinition(type) ? type : null;
};
